import { Close, Plus } from 'assets/icons';
import AddressGeoApi from 'components/AddressGeoApi/AddressGeoApi';
import Button from 'components/button';
import { cardIconButtonClass } from 'components/card/PanelCard';
import { agreementsOf, draftFromCompany, statusOptionsOf } from 'components/companyForm/companyLegalForm';
import JustificatifConfirmation, { CONFIRMATION_TITLE } from 'components/justificatifConfirmation/JustificatifConfirmation';
import useModal from 'components/modal/useModal';
import { PanelCardSkeleton } from 'components/skeleton/Skeleton';
import SlideOverBody from 'components/SlideOver/component/body';
import SlideOverFooter from 'components/SlideOver/component/footer';
import SlideOverHeader from 'components/SlideOver/component/header';
import useSlideOver from 'components/SlideOver/useSlideOver';
import FieldLabel from 'components/styledInput/FieldLabel';
import PillTabs from 'components/tabs/PillTabs';
import StyledInput from 'components/styledInput/StyledInput';
import TextSelectMultipleBigAccount from 'components/textSelectMultiple/textSelectMultipleBigAccount';
import { buildCompanySupplierFormOptions } from 'constants/missionSupplierFilters';
import {
  diffCompanyOverview,
  isCompanyOverviewValid,
  normalizePennylaneCategoryId,
  validateCompanyOverview,
} from 'helper/companyOverviewDiff';
import { callApi } from 'helper/promises';
import { useNotifyCompaniesChanged } from 'hooks/useCompaniesChanged';
import UseContext from 'hooks/useContext';
import useJustificatifConfirmation from 'hooks/useJustificatifConfirmation';
import { useNotifyRecordChanged } from 'hooks/useRecordChanged';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import ApiDatabase from 'server';

export const EDIT_COMPANY_PANEL = 'edit_company';

const sameCodes = (one, other) => one.length === other.length && one.every((code) => other.includes(code));
const quiet = (err) => {
  if (err?.name !== 'CanceledError') console.error(err);
};

// The form's tabs: a setting found by its part, all of them saved at once.
const TABS = [
  { id: 'legal', label: 'Informations légales' },
  { id: 'activity', label: 'Activité' },
  { id: 'installations', label: 'Installations' },
  { id: 'costs', label: 'Estimations de coût' },
];

const Section = ({ children }) => <section className="flex flex-col gap-4">{children}</section>;

/**
 * « Modifier l'entreprise » (slide-over `edit_company`, `objID`: the
 * company): every setting of the company in one form, in place of the read
 * view and its four modals. The legal information, the Pennylane sector, the
 * collective installations and the cost estimates are written as those modals
 * write them (postUpdateCompanyOverview, postCollectiveInstallationsCompany,
 * putDisplayCostEstimate); a change of the legal information passes through
 * the justificatif first (JBWEB-1214). The big account saves as soon as it is
 * picked, as in the activity modal.
 */
const CompanyEditPanel = () => {
  const { objID, refreshCompanyBigAccount, setRefreshCompanyBigAccount, modalDataReturn, setModalDataReturn } =
    UseContext();
  const companyId = objID ? String(objID) : null;
  const { toggleSlideOver } = useSlideOver();
  const { toggle } = useModal();
  const notifyRecord = useNotifyRecordChanged();
  const notifyCompanies = useNotifyCompaniesChanged();
  const justificatif = useJustificatifConfirmation();
  const token = localStorage.getItem('xsrfToken');

  const [company, setCompany] = useState(null);
  const [failed, setFailed] = useState(false);
  const [draft, setDraft] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState(null);
  const [referential, setReferential] = useState([]);
  const [installed, setInstalled] = useState([]);
  const [initialInstalled, setInitialInstalled] = useState([]);
  const [costEstimate, setCostEstimate] = useState(false);
  const [initialCostEstimate, setInitialCostEstimate] = useState(false);
  const [bigAccount, setBigAccount] = useState('');
  // The legal fields being confirmed, frozen on the way into the step.
  const [pendingFields, setPendingFields] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('legal');

  const close = () => toggleSlideOver();

  useEffect(() => {
    if (!companyId) return;
    ApiDatabase.getCompanyInfo(
      { idCompany: companyId },
      (data) => {
        const info = data?.company ?? null;
        if (!info) return setFailed(true);
        setCompany(info);
        setDraft(draftFromCompany(info));
        setCategory(normalizePennylaneCategoryId(info.pennylane_category_id));
        ApiDatabase.getCollectiveAgreement({ token }, (list) => setAgreements(agreementsOf(list, info)), quiet);
      },
      (err) => {
        if (err?.name !== 'CanceledError') setFailed(true);
      },
    );
    ApiDatabase.getCompanySuppliers({}, (data) => setSuppliers(Array.isArray(data) ? data : []), quiet);
    ApiDatabase.getCollectiveInstallations({ token }, (data) => setReferential(data?.installations ?? []), quiet);
    ApiDatabase.getCollectiveInstallationsCompany(
      { token, idCompany: companyId },
      (data) => {
        const codes = (data?.installations ?? []).map((installation) => installation.code);
        setInstalled(codes);
        setInitialInstalled(codes);
      },
      quiet,
    );
    ApiDatabase.getDisplayCostEstimate(
      { token, idCompany: companyId },
      (data) => {
        setCostEstimate(Boolean(data?.displayCostEstimate));
        setInitialCostEstimate(Boolean(data?.displayCostEstimate));
      },
      quiet,
    );
  }, [companyId, token]);

  const loadCategories = () =>
    ApiDatabase.getPennyLaneCategories({ token }, (data) => setCategories(data?.items || []), quiet);
  useEffect(() => {
    loadCategories();
  }, [token]);

  // A category created from the sector's list is read back, and picked.
  useEffect(() => {
    if (!modalDataReturn?.createdCategory) return;
    loadCategories();
    setModalDataReturn({});
  }, [modalDataReturn]);

  useEffect(() => {
    if (!companyId) return;
    ApiDatabase.getCompanyBigAccount({ token, idCompany: companyId }, (data) => setBigAccount(data?.bigAccount ?? ''), quiet);
  }, [companyId, token, refreshCompanyBigAccount]);

  if (failed) {
    return (
      <>
        <SlideOverHeader hide={close} closeLabel="Fermer">
          Modifier l’entreprise
        </SlideOverHeader>
        <SlideOverBody>
          <p className="text-sm text-gray-500">Les informations de l’entreprise n’ont pas pu être chargées.</p>
        </SlideOverBody>
      </>
    );
  }

  const ready = Boolean(company && draft);
  const errors = ready ? validateCompanyOverview(draft) : {};
  const legalFields = ready ? diffCompanyOverview(draft, company) : {};
  const legalChanged = Object.keys(legalFields).length > 0;
  const categoryChanged =
    ready && normalizePennylaneCategoryId(category) !== normalizePennylaneCategoryId(company.pennylane_category_id);
  const installationsChanged = !sameCodes(installed, initialInstalled);
  const costChanged = costEstimate !== initialCostEstimate;
  const canSave =
    ready && isCompanyOverviewValid(errors) && (legalChanged || categoryChanged || installationsChanged || costChanged);

  const editField = (field) => (event) => setDraft((previous) => ({ ...previous, [field]: event.target.value }));
  const selectAgreement = (updated) => {
    const selected = updated.find((agreement) => agreement.checked);
    setAgreements((previous) => previous.map((agreement) => ({ ...agreement, checked: agreement.id === selected?.value })));
    setDraft((previous) => ({ ...previous, conv_collective: selected ? selected.value : null }));
  };
  const toggleInstallation = (code) =>
    setInstalled((previous) => (previous.includes(code) ? previous.filter((one) => one !== code) : [...previous, code]));
  const deleteBigAccount = () =>
    ApiDatabase.deleteCompanyBigAccount(
      { token, idCompany: companyId },
      () => {
        toast.success('Le grand compte a bien été supprimé');
        setBigAccount('');
        setRefreshCompanyBigAccount((previous) => !previous);
      },
      () => toast.error('Le grand compte n’a pas pu être retiré.'),
    );

  const save = async (storedJustificatif = null) => {
    setSaving(true);
    try {
      const overview = {
        ...(pendingFields ?? legalFields),
        ...(categoryChanged ? { pennylane_category_id: normalizePennylaneCategoryId(category) } : {}),
      };
      if (Object.keys(overview).length > 0) {
        await callApi(ApiDatabase.postUpdateCompanyOverview, {
          token,
          idCompany: companyId,
          companyInfo: overview,
          ...(storedJustificatif ? { justificatif: storedJustificatif } : {}),
        });
      }
      if (installationsChanged) {
        await callApi(ApiDatabase.postCollectiveInstallationsCompany, { token, idCompany: companyId, codes: installed });
      }
      if (costChanged) {
        await callApi(ApiDatabase.putDisplayCostEstimate, { token, idCompany: companyId, displayCostEstimate: costEstimate });
      }
      toast.success('L’entreprise a été mise à jour.');
      notifyRecord();
      notifyCompanies();
      close();
    } catch (err) {
      if (err?.name === 'CanceledError') return;
      console.error(err);
      toast.error(err?.response?.data?.message ?? 'L’entreprise n’a pas pu être mise à jour.');
    } finally {
      setSaving(false);
    }
  };

  // The legal information identifies the company on its contracts: its
  // change carries the client's written request (JBWEB-1214).
  const submit = () => {
    if (justificatif.isConfirming) return justificatif.confirm(save);
    if (!canSave) return undefined;
    if (legalChanged) {
      setPendingFields(legalFields);
      return justificatif.setIsConfirming(true);
    }
    return save();
  };

  const selectedCategory = categories.find(
    (one) => normalizePennylaneCategoryId(one.id) === normalizePennylaneCategoryId(category),
  );
  const categoryOptions = [
    { value: null, label: 'Aucune catégorie', checked: normalizePennylaneCategoryId(category) === null },
    ...categories.map((one) => ({
      value: one.id,
      label: one.label,
      checked: normalizePennylaneCategoryId(one.id) === normalizePennylaneCategoryId(category),
    })),
  ];

  const form = ready ? (
    <div className="flex flex-col gap-8">
      {tab === 'legal' && (
      <Section>
        <StyledInput type="text" label="Raison sociale" value={draft.reason_social} onChange={editField('reason_social')} />
        <StyledInput type="text" label="Nom commercial" value={draft.name_commercial} onChange={editField('name_commercial')} />
        <div className="grid grid-cols-2 gap-4">
          <StyledInput type="number" label="SIRET" value={draft.siret} onChange={editField('siret')} error={errors.errorSiret} />
          <StyledInput type="select" label="Forme juridique" value={draft.status} list={statusOptionsOf(company)} onChange={editField('status')} />
          <StyledInput type="text" label="Code APE" value={draft.code_ape} onChange={editField('code_ape')} error={errors.errorCodeApe} />
          <StyledInput type="number" label="Effectif" value={draft.staff} onChange={editField('staff')} error={errors.errorStaff} />
        </div>
        <StyledInput
          type="select"
          label="Fournisseur"
          value={draft.companySupplier || ''}
          list={buildCompanySupplierFormOptions(suppliers)}
          onChange={(event) => setDraft((previous) => ({ ...previous, companySupplier: event.target.value || null }))}
        />
        <div className="flex flex-col gap-1">
          <FieldLabel label="Adresse" />
          <AddressGeoApi
            addressGeoValue={(address) => setDraft((previous) => ({ ...previous, address }))}
            addressValue={draft.address}
            page="editCompany"
          />
        </div>
        <StyledInput
          type="selectMultipleRadio"
          label="Convention collective"
          value={agreements.find((agreement) => agreement.checked)?.name || '-'}
          list={agreements.map((agreement) => ({ value: agreement.id, label: agreement.name, checked: agreement.checked }))}
          onChange={selectAgreement}
          isSearchable
          dropdownFullWidth
        />
      </Section>
      )}

      {tab === 'activity' && (
      <Section>
        <StyledInput
          type="selectMultipleRadio"
          label="Catégorie"
          isSearchable
          value={normalizePennylaneCategoryId(category) === null ? 'Aucune catégorie' : selectedCategory?.label || '-'}
          list={categoryOptions}
          dropdownFullWidth
          onChange={(updated) => setCategory(normalizePennylaneCategoryId(updated.find((option) => option.checked)?.value ?? null))}
          footer={
            <div className="flex cursor-pointer items-center gap-1 p-1" onClick={() => toggle('company_pennylane_create_category')}>
              <Plus wh={20} color="#374151" />
              Créer une catégorie
            </div>
          }
        />
        <div className="flex flex-col gap-1">
          {bigAccount ? (
            <>
              <FieldLabel label="Grand compte" />
              <div className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
                <span className="text-sm font-medium text-gray-900">{bigAccount?.name}</span>
                <button type="button" aria-label="Retirer le grand compte" className={cardIconButtonClass} onClick={deleteBigAccount}>
                  <Close wh={18} color="#374151" />
                </button>
              </div>
            </>
          ) : (
            // The selector carries its own « Grand compte » label.
            <TextSelectMultipleBigAccount />
          )}
          <p className="text-xs text-gray-500">Le grand compte est enregistré dès sa sélection.</p>
        </div>
      </Section>
      )}

      {tab === 'installations' && (
      <Section>
        <div className="min-w-0 overflow-hidden rounded-lg border border-gray-100 bg-white">
          <ul className="divide-y divide-gray-100">
            {referential.map((installation) => (
              <li key={installation.code} className="flex px-5 py-4 hover:bg-gray-50">
                <StyledInput
                  type="checkbox"
                  label={installation.label}
                  checked={installed.includes(installation.code)}
                  onChange={() => toggleInstallation(installation.code)}
                />
              </li>
            ))}
          </ul>
        </div>
      </Section>
      )}

      {tab === 'costs' && (
      <Section>
        <StyledInput
          type="checkbox"
          label="Le client voit les estimations de coût"
          checked={costEstimate}
          onChange={() => setCostEstimate((previous) => !previous)}
        />
      </Section>
      )}
    </div>
  ) : (
    <div className="flex flex-col gap-4">
      <PanelCardSkeleton title="Informations légales" rows={5} />
      <PanelCardSkeleton title="Activité de l’entreprise" rows={2} />
    </div>
  );

  return (
    <>
      <SlideOverHeader
        hide={close}
        closeLabel="Fermer"
        description={company?.name_commercial || company?.reason_social || null}
      >
        {justificatif.isConfirming ? CONFIRMATION_TITLE : 'Modifier l’entreprise'}
      </SlideOverHeader>
      {/* The tabs, under the title; the confirmation step has none. */}
      {!justificatif.isConfirming && (
        <div className="flex-shrink-0 px-6 pb-3">
          <PillTabs
            wrap
            activeTab={tab}
            onChange={setTab}
            // A tab holding a field to correct says how many.
            tabs={TABS.map((one) => ({
              ...one,
              count: one.id === 'legal' && !isCompanyOverviewValid(errors) ? Object.values(errors).filter(Boolean).length : undefined,
            }))}
          />
        </div>
      )}
      <SlideOverBody>
        {justificatif.isConfirming ? (
          <JustificatifConfirmation
            file={justificatif.file}
            setFile={justificatif.setFile}
            isAttested={justificatif.isAttested}
            setIsAttested={justificatif.setIsAttested}
            error={justificatif.error}
            description="Ces informations identifient l'entreprise sur ses contrats. Un justificatif est requis avant validation."
          />
        ) : (
          form
        )}
      </SlideOverBody>
      <div className="flex-shrink-0 border-t border-gray-100">
        <SlideOverFooter>
          <Button
            size="SM"
            color="SECONDARY"
            // Backs out of the confirmation, not the panel.
            onClick={
              justificatif.isConfirming
                ? () => {
                    setPendingFields(null);
                    justificatif.setIsConfirming(false);
                  }
                : close
            }
          >
            Annuler
          </Button>
          {saving || justificatif.isUploading ? (
            <Button size="SM" color="DISABLED">
              Enregistrement…
            </Button>
          ) : (
            <Button
              size="SM"
              color={(justificatif.isConfirming ? justificatif.canConfirm : canSave) ? 'PRIMARY' : 'DISABLED'}
              disabled={!(justificatif.isConfirming ? justificatif.canConfirm : canSave)}
              onClick={submit}
            >
              Enregistrer
            </Button>
          )}
        </SlideOverFooter>
      </div>
    </>
  );
};

export default CompanyEditPanel;
